import React, { forwardRef } from 'react';
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup as MuiRadioGroup,
} from '@material-ui/core';
import { useDsTokens } from '../theme/useDsTokens';
import { cn } from '../utils/cn';
import { DsCommonProps, DsValidationProps, RadioOption } from './types';

export interface RadioGroupProps extends DsCommonProps, DsValidationProps {
  name: string;
  value?: string;
  defaultValue?: string;
  options?: RadioOption[];
  row?: boolean;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void;
  children?: React.ReactNode;
}

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(function RadioGroup(
  {
    id,
    className,
    disabled,
    error,
    required,
    name,
    value,
    defaultValue,
    options,
    row,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    onChange,
    children,
    'data-testid': testId,
  },
  ref
) {
  const tokens = useDsTokens();

  return (
    <FormControl
      component="fieldset"
      disabled={disabled}
      required={required}
      error={error}
      className={cn(className)}
      data-testid={testId}
    >
      <MuiRadioGroup
        ref={ref}
        id={id}
        name={name}
        value={value}
        defaultValue={defaultValue}
        row={row}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-invalid={error ? true : undefined}
        onChange={onChange}
      >
        {options
          ? options.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                disabled={option.disabled || disabled}
                control={
                  <Radio
                    color="primary"
                    style={{
                      color: error ? tokens.statusError : tokens.brandPrimary,
                    }}
                  />
                }
                label={option.label}
                style={{
                  color: disabled || option.disabled ? tokens.textDisabled : tokens.textPrimary,
                  fontSize: tokens.fontSizeBody,
                }}
              />
            ))
          : children}
      </MuiRadioGroup>
    </FormControl>
  );
});

RadioGroup.displayName = 'RadioGroup';

export { Radio } from '@material-ui/core';
